/**
 * Voice AI — LLM Provider Service
 * Owns all LLM provider API calls. Components call this service
 * instead of making direct fetch calls.
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';

class LlmService {

  /* ─── API Calls ─── */

  /**
   * List all configured LLM providers.
   * @returns {Promise<Object>} { success, providers[], default_key }
   */
  async listProviders() {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php`);
    return res.json();
  }

  /**
   * Create a new LLM provider.
   * @param {Object} data - { provider, label, api_key, model, temperature?, max_tokens?, base_url? }
   * @returns {Promise<Object>} { success, key }
   */
  async createProvider(data) {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  /**
   * Update an existing LLM provider.
   * @param {string} key - Provider key suffix
   * @param {Object} data - Fields to update (api_key only sent if changed)
   * @returns {Promise<Object>} { success }
   */
  async updateProvider(key, data) {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php?key=${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  /**
   * Delete an LLM provider.
   * @param {string} key - Provider key suffix
   * @returns {Promise<Object>} { success }
   */
  async deleteProvider(key) {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php?key=${encodeURIComponent(key)}`, {
      method: 'DELETE'
    });
    return res.json();
  }

  /**
   * Set a provider as the default.
   * @param {string} key - Provider key suffix
   * @returns {Promise<Object>} { success, default_key }
   */
  async setDefault(key) {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key })
    });
    return res.json();
  }

  /**
   * Set (or disable) the contextual LLM default.
   * @param {string} key - Provider key suffix, or '' to disable
   * @returns {Promise<Object>} { success, contextual_default_key }
   */
  async setContextualDefault(key) {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php?scope=contextual`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key })
    });
    return res.json();
  }

  /* ─── Contextual LLM Provider CRUD ─── */

  async listContextualProviders() {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php?scope=contextual`);
    return res.json();
  }

  async createContextualProvider(data) {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php?scope=contextual`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  async updateContextualProvider(key, data) {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php?scope=contextual&key=${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  async deleteContextualProvider(key) {
    const res = await authService.apiFetch(`${API_BASE}/llm-providers.php?scope=contextual&key=${encodeURIComponent(key)}`, {
      method: 'DELETE'
    });
    return res.json();
  }

  /* ─── Provider Definitions ─── */

  /**
   * Get the list of supported provider types and their models.
   * Providers with hasBrowseModels fetch live models from OpenRouter catalog.
   * @returns {Array<Object>}
   */
  getProviderDefinitions() {
    return [
      {
        id: 'openrouter',
        name: 'OpenRouter',
        icon: 'fa-globe',
        models: [],
        placeholder: 'sk-or-...',
        hasBrowseModels: true
      },
      {
        id: 'openai',
        name: 'OpenAI',
        icon: 'fa-microchip-ai',
        models: [],
        placeholder: 'sk-proj-...',
        hasBrowseModels: true
      },
      {
        id: 'anthropic',
        name: 'Anthropic',
        icon: 'fa-brain',
        models: [],
        placeholder: 'sk-ant-...',
        hasBrowseModels: true
      },
      {
        id: 'google',
        name: 'Google',
        icon: 'fa-gem',
        models: [],
        placeholder: 'AIza...',
        hasBrowseModels: true
      },
      {
        id: 'xai',
        name: 'xAI (Grok)',
        icon: 'fa-robot',
        models: [],
        placeholder: 'xai-...',
        hasBrowseModels: true
      },
      // ── Hidden from selection (uncomment to re-enable) ──
      // {
      //   id: 'groq',
      //   name: 'Groq',
      //   icon: 'fa-bolt',
      //   models: [
      //     'llama-3.3-70b-versatile',
      //     'llama-3.1-8b-instant',
      //     'openai/gpt-oss-120b',
      //     'openai/gpt-oss-20b',
      //     'meta-llama/llama-4-scout-17b-16e-instruct',
      //     'qwen/qwen3-32b',
      //     'moonshotai/kimi-k2-instruct-0905'
      //   ],
      //   placeholder: 'gsk_...'
      // },
      // {
      //   id: 'together',
      //   name: 'Together AI',
      //   icon: 'fa-layer-group',
      //   models: [
      //     'meta-llama/Llama-3.3-70B-Instruct-Turbo',
      //     'meta-llama/Llama-4-Scout-17B-16E-Instruct',
      //     'Qwen/Qwen3-235B-A22B-fp8',
      //     'mistralai/Mixtral-8x7B-Instruct-v0.1',
      //     'deepseek-ai/DeepSeek-R1'
      //   ],
      //   placeholder: ''
      // },
      {
        id: 'local_claude_mcp',
        name: 'Claude CLI + MCP',
        icon: 'fa-wand-magic-sparkles',
        models: [
          'sonnet',
          'opus',
          'haiku',
        ],
        placeholder: '',
        noApiKey: true,
        hasMcpConfig: true
      },
      {
        id: 'custom',
        name: 'Custom',
        icon: 'fa-code',
        models: [],
        placeholder: '',
        hasBaseUrl: true
      }
    ];
  }

  /**
   * Get models for a given provider ID (hardcoded fallback list).
   * @param {string} providerId
   * @returns {string[]}
   */
  getModelsForProvider(providerId) {
    const def = this.getProviderDefinitions().find(p => p.id === providerId);
    return def ? def.models : [];
  }

  /**
   * Get provider definition by ID.
   * Also checks commented-out providers so existing configs still resolve.
   * @param {string} providerId
   * @returns {Object|null}
   */
  getProviderDef(providerId) {
    // Check active definitions first
    const active = this.getProviderDefinitions().find(p => p.id === providerId);
    if (active) return active;
    // Fallback for hidden providers (existing configs still need icon/name)
    const hidden = {
      groq: { id: 'groq', name: 'Groq', icon: 'fa-bolt' },
      together: { id: 'together', name: 'Together AI', icon: 'fa-layer-group' },
    };
    return hidden[providerId] || null;
  }

  /* ─── Model Browser (server-side via llm-models.php) ─── */

  /**
   * Fetch models for a provider.
   * Direct providers (openai, anthropic, google, xai): POST with API key → native provider API.
   * OpenRouter/all: GET → public OpenRouter catalog (cached 24h).
   * @param {string} provider - Provider ID or 'all'
   * @param {string} apiKey - User's API key (required for direct providers)
   * @param {boolean} refresh - Force cache refresh (OpenRouter only)
   * @returns {Promise<Array<Object>>}
   */
  async fetchModelsForProvider(provider = 'all', apiKey = '', refresh = false, providerKey = '') {
    const isOpenRouter = !provider || provider === 'all' || provider === 'openrouter';

    let res;
    if (isOpenRouter) {
      // GET — public OpenRouter catalog
      const params = new URLSearchParams();
      if (refresh) params.set('refresh', '1');
      const url = `${API_BASE}/llm-models.php${params.toString() ? '?' + params : ''}`;
      res = await authService.apiFetch(url);
    } else {
      // POST — direct provider API with user's key (or stored key via provider_key)
      const payload = { provider };
      if (apiKey) payload.api_key = apiKey;
      if (providerKey) payload.provider_key = providerKey;
      res = await authService.apiFetch(`${API_BASE}/llm-models.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }

    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to load models');
    return data.models || [];
  }
}

export const llmService = new LlmService();
